import { matchPath } from 'react-router-dom';

import { getLegacyGeoAreaIdFromUrlParams } from '@/pages/AuctionsNearMe/helpers/geoAreaToUrlParams';
import { isPlausibleLocation } from '@/pages/AuctionsNearMe/helpers/isPlausibleLocation';
import { normalizeSoft404Path } from '@/utils/urls';

/**
 * True for a `/find-auctions-near-me/:state/:city/` URL whose segments cannot describe a real
 * place. Reaches the same verdict as the page loader's pre-fetch rejection, but from the path
 * alone — so the client can render the 404 on its first pass, without waiting for a loader to
 * run and without depending on redux state that may not have survived the trip to the browser.
 *
 * Single-segment URLs are not decidable from the path (a segment with no GeoArea record can
 * still be a real place), so they are left to the loader's `invalidLocationPath`.
 */
export const isMalformedAuctionsNearMeUrl = (path: string = ''): boolean => {
    const match = matchPath('/find-auctions-near-me/:state/:city', normalizeSoft404Path(path));

    if (!match) {
        return false;
    }

    const { city = '', state = '' } = match.params;

    // Legacy /{geoAreaId}/{geoAreaName}/ links carry an id in the state segment, so the
    // plausibility check would reject every one of them.
    if (getLegacyGeoAreaIdFromUrlParams({ city, state })) {
        return false;
    }

    return !isPlausibleLocation(city, state);
};
